import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AuditLogEventApiResponse } from 'src/app/models/api/accountManagement.model';
import { AuditLogIconService } from 'src/app/shared/services/audit-log-icon.service';

@Component({
  selector: 'app-audit-log-event-details-dialog',
  templateUrl: './audit-log-event-details-dialog.component.html',
  styleUrls: ['./audit-log-event-details-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AuditLogEventDetailsDialogComponent {
  public dialogRef = inject(MatDialogRef<AuditLogEventDetailsDialogComponent>);
  public data = inject<AuditLogEventApiResponse>(MAT_DIALOG_DATA);
  private readonly _auditLogIconService = inject(AuditLogIconService);
  private readonly _router = inject(Router);

  private readonly _truncateStartChars = 8;
  private readonly _truncateEndChars = 5;

  formatMetadata(metadata: Record<string, unknown>): string {
    const lines = Object.entries(metadata).map(([key, value]) => {
      const readableKey = key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
      const val =
        value == null
          ? ''
          : Array.isArray(value)
            ? value.join(', ')
            : typeof value === 'object'
              ? JSON.stringify(value)
              : String(value);
      return `${readableKey}: ${val}`;
    });
    return lines.length === 1 ? lines[0] : lines.map((l) => `${l}.`).join('\n');
  }

  getObjectTypeIcon(objectType?: string): string {
    return this._auditLogIconService.getObjectTypeIcon(objectType, false);
  }

  formatEntityType(entityType?: string): string {
    if (!entityType) return '';
    return entityType.charAt(0)?.toUpperCase() + entityType.slice(1)?.toLowerCase();
  }

  formatEntityAction(entityAction?: string): string {
    if (!entityAction) return '';
    return entityAction.charAt(0)?.toUpperCase() + entityAction.slice(1)?.toLowerCase();
  }

  isEntityIdLinkable(): boolean {
    const entityType = this.data.entityType?.toLowerCase();
    const entityAction = this.data.entityAction?.toLowerCase();
    const entityId = this.data.metadata?.entity_id;
    const parentId = this._getParentId();

    if (!entityId) return false;

    // Case 1: Agent_step type requires parent_id for link
    if (entityType === 'agent_step' && parentId) return true;

    // Case 2: Get_thread_activities action
    if (entityAction === 'get_thread_activities') return true;

    // Case 3: Finding type requires parent_id for link
    if (entityType === 'finding' && parentId) return true;

    return false;
  }

  getEntityIdUrl(): string | null {
    const entityType = this.data.entityType?.toLowerCase();
    const entityAction = this.data.entityAction?.toLowerCase();
    const entityId = this.data.metadata?.entity_id;
    const parentId = this._getParentId();

    if (!entityId) return null;

    // Case 1: Agent_step -> ai-activity
    if (entityType === 'agent_step' && parentId) {
      return `/app/ai-activity?q=&integration=${parentId}&agentStep=${entityId}`;
    }

    // Case 2: Get_thread_activities -> ai-findings
    if (entityAction === 'get_thread_activities') {
      return `/app/ai-findings?q=severity:high,medium%20tag:generalAvailability,preview&findingThreadId=${entityId}`;
    }

    // Case 3: Finding -> ai-findings
    if (entityType === 'finding' && parentId) {
      return `/app/ai-findings?q=severity:high,medium%20tag:generalAvailability,preview&view=individuals&integration=${parentId}&finding=${entityId}`;
    }

    return null;
  }

  truncateEntityId(entityId: string): string {
    if (!entityId) return '';
    const threshold = this._truncateStartChars + this._truncateEndChars + 3;
    if (entityId.length <= threshold) {
      return entityId;
    }
    const start = entityId.substring(0, this._truncateStartChars);
    const end = entityId.substring(entityId.length - this._truncateEndChars);
    return `${start}...${end}`;
  }

  navigateToEntity(): void {
    const url = this.getEntityIdUrl();
    if (url) {
      this.dialogRef.close();
      this._router.navigateByUrl(url);
    }
  }

  getOtherMetadata(): Record<string, unknown> | null {
    if (!this.data.metadata) return null;

    const { entity_id: _entityId, ...rest } = this.data.metadata;
    return Object.keys(rest).length > 0 ? rest : null;
  }

  getMetadataBeforeEntityId(): Record<string, unknown> | null {
    if (!this.data.metadata) return null;

    const result: Record<string, unknown> = {};
    const targetKeys = ['parent_id', 'entity_parent_id', 'parent_type', 'entity_parent_type'];

    Object.entries(this.data.metadata).forEach(([key, value]) => {
      if (key !== 'entity_id' && targetKeys.includes(key)) {
        result[key] = value;
      }
    });

    return Object.keys(result).length > 0 ? result : null;
  }

  getMetadataAfterEntityId(): Record<string, unknown> | null {
    if (!this.data.metadata) return null;

    const result: Record<string, unknown> = {};
    const excludeKeys = ['parent_id', 'entity_parent_id', 'parent_type', 'entity_parent_type', 'entity_id'];

    Object.entries(this.data.metadata).forEach(([key, value]) => {
      if (!excludeKeys.includes(key)) {
        result[key] = value;
      }
    });

    return Object.keys(result).length > 0 ? result : null;
  }

  private _getParentId(): string | undefined {
    const metadata = this.data.metadata as Record<string, unknown> | undefined;
    if (!metadata) return undefined;
    // Try both possible field names: parent_id (from API) and entity_parent_id (from model)
    return (metadata['parent_id'] as string) || (metadata['entity_parent_id'] as string) || undefined;
  }
}
